<script setup lang="ts">
import { computed, ref } from 'vue';
import SearchIcon from 'vue-material-design-icons/Magnify.vue';
import { navigateTo } from '#app/composables/router';

type TabKey = 'news' | 'recipe';

defineProps({
  isShow: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const keyword = ref('');
const activeTab = ref<TabKey>('news');

const tabs: Array<{
  text: string;
  key: TabKey;
}> = [
  {
    text: '記事',
    key: 'news',
  },
  {
    text: 'レシピ',
    key: 'recipe',
  },
];

function close() {
  emit('close');
}

async function submit() {
  await navigateTo({
    path: `/public/article/${activeTab.value}`,
    query: {
      s: keyword.value,
    },
  });
}

const placeholder = computed(() => {
  switch (activeTab.value) {
    case 'recipe':
      return '料理名・食材';
    default:
      return 'キーワード';
  }
});
</script>

<template>
  <div
    v-if="isShow"
    class="fixed w-screen h-screen bg-black/40 top-0 right-0 z-40"
    data-testid="overlay"
    @click="close"
  />

  <Transition>
    <div
      v-if="isShow"
      class="fixed w-[18rem] h-screen top-0 right-0 z-50 border-t-2 border-t-[#ad948b] bg-[#f6f3f2]"
    >
      <h2
        class="heading bg-beige-400 border-b border-[#e6dedb] px-2 py-1 text-[#ad948b] font-bold"
      >
        検索
      </h2>

      <div class="tab-panel flex px-3 pt-3 gap-3 border-b border-b-[#e6dedb]">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="font-bold text-gray-500 w-full bg-white py-2 border-t-2 border-t-[#e6dedb] border-l border-l-[#e6dedb] border-r border-r-[#e6dedb] rounded-t-lg"
          :class="{
            'border-t-[#f46090]': activeTab === tab.key,
          }"
          date-testid="tab-button"
          @click="activeTab = tab.key"
        >
          {{ tab.text }}
        </button>
      </div>

      <div class="pb-6 px-3 pt-2">
        <form
          class="px-5 py-2.5 rounded-lg border border-[#e6dedb] bg-white flex items-center"
          @submit.prevent="submit"
        >
          <input
            v-model="keyword"
            type="text"
            :placeholder="placeholder"
            class="grow block appearance-none placeholder-[#aaa]"
            date-testid="input"
          />
          <button class="shrink-0 color-[#ad948b]">
            <SearchIcon :size="20" />
          </button>
        </form>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: transform 0.4s ease;
}

.v-enter-from,
.v-leave-to {
  transform: translateX(100%);
}

.heading {
  box-shadow: inset 0 1px 0 0 #fff;
  text-shadow: 0 1px 0 #fff;
}

.tab-panel {
  box-shadow: inset 0 1px 0 0 #fff;
}
</style>
